import { Typography, Checkbox, FormControlLabel } from "@mui/material";
import parse, {
  Element,
  DOMNode,
  domToReact,
  HTMLReactParserOptions,
} from "html-react-parser";
import {
  checkDomainCode,
  decodeHtmlEntities,
  isCaPostalCode,
  isUsPostalCode,
} from "../Helper/Helper";
import { Errors, Field, FormValues } from "../../../Interface/scheduler/form";
import { useFormContext } from "../../contexts/FormContext";
import { AppConstants } from "../../../constants/constant";
import { getPricingValidationError } from "../FieldValidator/validateFields";
import "./CheckBoxField.css";

interface CheckBoxFieldProps {
  field: Field;
  errors: Errors;
  isServicePage?: boolean;
  setErrors?: React.Dispatch<React.SetStateAction<Errors>>;
  setClickedTermsandConditions?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CheckBoxField = ({
  field,
  isServicePage,
  errors,
  setErrors,
  setClickedTermsandConditions,
}: CheckBoxFieldProps) => {
  const { formValues, setFormValues } = useFormContext();
  const getDescription = (field: Field, domain: string): string => {
    const isCa = domain === "ca";
    return (
      decodeHtmlEntities(
        isCa
          ? isUsPostalCode((formValues?.zipcode as string) || "")
            ? field?.description
            : field?.cadescription
          : isCaPostalCode((formValues?.zipcode as string) || "")
            ? field?.cadescription
            : field?.description
      ) || ""
    );
  };

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (isServicePage) {
      e.preventDefault();
      setClickedTermsandConditions(true);
    }
  };

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        domNode.type === "tag" &&
        domNode.name === "a" &&
        domNode.attribs &&
        domNode.attribs.href
      ) {
        const url = domNode.attribs.href;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => handleAnchorClick(e)}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {domToReact((domNode as Element).children as DOMNode[], options)}
          </a>
        );
      }
    },
  };

  const getLabel = (
    field: Field,
    formValues: FormValues,
    isCaDomain: boolean
  ): string => {
    return isCaDomain
      ? isUsPostalCode((formValues?.zipcode as string) || "")
        ? field?.label
        : field?.calabel
      : isCaPostalCode((formValues?.zipcode as string) || "")
        ? field?.calabel
        : field?.label;
  };

  const renderLabel = (labelText: string) => {
    return (
      <span data-testid="checkbox-label">{parse(labelText, options)}</span>
    );
  };

  const renderDescription = (field: Field, domain: string) => {
    const description = getDescription(field, domain);
    return (
      <Typography
        component="div"
        className={`terms-policy-text ${field.id}-description`}
        variant="body2"
        data-testid="checkbox-description"
      >
        {parse(description)}
      </Typography>
    );
  };
  const domain = checkDomainCode();
  const isCaDomain = domain === "ca";
  if (isCaDomain) {
    if (!field?.calabel) {
      return null;
    }
  } else {
    if (!field?.label) {
      return null;
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [field.id]: e.target.checked ? "true" : "false",
    }));
    if (isServicePage) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field.id]: getPricingValidationError(field, field.id),
      }));
    }
  };

  const displayIsExistingCustomer =
    field.id !== AppConstants.EXISTING_CUSTOMER ||
    !!field.displayExistingCustomer;

  const labelText = getLabel(field, formValues, isCaDomain);

  return (
    labelText &&
    displayIsExistingCustomer && (
      <div
        className="input-label-wrap checkbox"
        id={field.id}
        key={field.id}
        style={{
          flex: field.width === "100%" ? "inherit" : undefined,
        }}
        data-testid="checkbox-field-root"
      >
        <FormControlLabel
          className="checkbox-basic-reminder"
          control={
            <Checkbox
              name={field.id}
              id={field.id}
              className={`checkbox-basic ${errors[field.id] ? "error" : ""}`}
              checked={formValues[field.id] === "true"}
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                  color: (theme) => theme.palette.primary.main,
                },
              }}
              onChange={(e) => handleChange(e)}
              color="primary"
              data-testid="checkbox-input"
            />
          }
          label={renderLabel(labelText)}
        />
        {(field.cadescription || field.description) &&
          renderDescription(field, domain)}
        {errors?.[field.id] && (
          <div
            className="error-message"
            data-testid={"error-message-" + field.id}
          >
            {errors[field.id]}
          </div>
        )}
      </div>
    )
  );
};

export default CheckBoxField;
